import {
  createRowFilter,
  DataTable,
  DurationFilterInput,
  getGridNumericOperators,
  getGridSingleSelectOperators,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridColDef,
  GridColumnVisibilityModel,
  gridFilteredSortedRowIdsSelector,
  GridFilterModel,
  GridRenderCellParams,
  GridSortModel,
  useGridApiRef,
} from '@components/DataTable';
import { PlaceholderGradient } from '@components/util/PlaceholderGradient';
import { UnfurlItem } from '@lib/external/unfurl';
import {
  applyVisibilityChange,
  isDeclaredVisible,
  mergeColumnDefinitions,
} from '@lib/external/unfurlColumns';
import {
  DuplicateCheckable,
  filterNonDuplicates,
  isMediaDuplicate,
} from '@lib/utils/chooseMediaUtils';
import { formatDateToLocale, formatOptionalDuration } from '@lib/utils/units';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { SelectChangeEvent, Tooltip } from '@mui/material';
import { useMediaStatusPolling } from '@pages/Library/hooks/useMediaStatusPolling';
import { trackingStore } from '@stores/Tracking';
import {
  computeMediaStatus,
  computeWarningPercentileDuration,
  MediaStatusType,
} from '@stores/UploadURL/mediaStatus';
import { useUploadURL } from '@stores/UploadURL/UploadURLStore';
import { observer } from 'mobx-react-lite';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDataGridSelection } from '../../../../hooks/useDataGridSelection';
import { useDuplicateChecker } from '../../../../hooks/useDuplicateChecker';
import { DuplicateStatus } from './DuplicateStatus';
import { HeaderMessage } from './HeaderStatusMessages';
import css from './MediaDataGrid.module.scss';
import { MediaDataGridFilterPopover } from './MediaDataGridFilterPopover';
import { MediaDataGridHeader } from './MediaDataGridHeader';
import { getStatusTooltip, MediaStatusBadge } from './MediaStatusBadge';
import { SORT_OPTIONS, SortOption } from './SortSelector';
import {
  buildUnfurlColumn,
  CURATED_UNFURL_KEYS,
  headerMinWidth,
  unfurlColumnField,
} from './unfurlGridColumns';

// Utility function to generate consistent ID from string
const generateIdFromString = (str: string): number => {
  return str.split('').reduce((acc, char) => {
    return acc + char.charCodeAt(0) * 37 + 1;
  }, 0);
};

const MemoizedThumbnail = ({
  video,
  hasThumbnailError,
  onThumbnailError,
}: {
  video: UnfurlItem;
  hasThumbnailError: boolean;
  onThumbnailError: (itemId: string) => void;
}) => {
  const thumbnailUrl = video.thumbnail;

  if (hasThumbnailError || !thumbnailUrl) {
    // Generate consistent ID from title for LQIP
    const lqipId = generateIdFromString(video.title);
    return (
      <div className={css.thumbnailContainer}>
        <PlaceholderGradient id={lqipId} blur={50} />
      </div>
    );
  }

  return (
    <div className={css.thumbnailContainer}>
      <img
        src={thumbnailUrl}
        alt={video.title}
        className={css.thumbnailImage}
        onError={() => onThumbnailError(video.id)}
      />
    </div>
  );
};
MemoizedThumbnail.displayName = 'MemoizedThumbnail';

// Helper function to highlight matching terms in text
const HighlightedText = ({ text, highlightTerms }: { text: string; highlightTerms: string[] }) => {
  if (highlightTerms.length === 0) {
    return <>{text}</>;
  }

  // Create a regex that matches any of the terms (case-insensitive)
  const escapedTerms = highlightTerms.map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const regex = new RegExp(`(${escapedTerms.join('|')})`, 'gi');

  // Split text by matches and wrap matches in highlight spans
  const parts = text.split(regex);

  return (
    <>
      {parts.map((part, index) => {
        const isMatch = highlightTerms.some((term) => part.toLowerCase() === term.toLowerCase());
        return isMatch ? (
          <span key={index} className={css.titleHighlight}>
            {part}
          </span>
        ) : (
          <span key={index}>{part}</span>
        );
      })}
    </>
  );
};

const MemoizedTitleCell = ({
  video,
  status,
  hasThumbnailError,
  onThumbnailError,
  highlightTerms = [],
}: {
  video: UnfurlItem;
  status: MediaStatusType | null;
  hasThumbnailError: boolean;
  onThumbnailError: (itemId: string) => void;
  highlightTerms?: string[];
}) => {
  return (
    <div className={css.titleCell}>
      <MemoizedThumbnail
        video={video}
        hasThumbnailError={hasThumbnailError}
        onThumbnailError={onThumbnailError}
      />
      <div className={css.titleContainer}>
        <span className={css.titleText}>
          <HighlightedText text={video.title} highlightTerms={highlightTerms} />
        </span>
        <MediaStatusBadge status={status} />
      </div>
    </div>
  );
};
MemoizedTitleCell.displayName = 'MemoizedTitleCell';

const MemoizedDurationCell = ({ video }: { video: UnfurlItem }) => {
  // Only show a duration when there's a meaningful one. Items with no real
  // duration — images, or anything the source reports as 0 (e.g. audio whose
  // length wasn't computed) — get the em-dash sentinel rather than "00:00",
  // which would read as a zero-length clip.
  const hasMeaningfulDuration = typeof video.duration === 'number' && video.duration > 0;
  return (
    <span className={css.durationCell}>
      {hasMeaningfulDuration ? formatOptionalDuration(video.duration) : '—'}
    </span>
  );
};
MemoizedDurationCell.displayName = 'MemoizedDurationCell';

const MemoizedPublishedDateCell = ({ video }: { video: UnfurlItem }) => {
  const publishedAt = 'publishedAt' in video ? video.publishedAt : undefined;
  return (
    <span className={css.publishedCell}>
      {publishedAt ? formatDateToLocale(publishedAt) : 'N/A'}
    </span>
  );
};
MemoizedPublishedDateCell.displayName = 'MemoizedPublishedDateCell';

const MemoizedInLibraryCell = ({
  video,
  isDuplicate,
}: {
  video: UnfurlItem;
  isDuplicate: (item: UnfurlItem) => boolean;
}) => {
  return (
    <span className={css.duplicateCell}>
      <DuplicateStatus
        item={video}
        isDuplicate={isDuplicate as (item: DuplicateCheckable) => boolean}
        showBadge={false}
        showIcon={true}
      />
    </span>
  );
};
MemoizedInLibraryCell.displayName = 'MemoizedInLibraryCell';

const MemoizedURLCell = ({ video }: { video: UnfurlItem }) => {
  return <span className={css.urlCell}>{video.url}</span>;
};
MemoizedURLCell.displayName = 'MemoizedURLCell';

const MemoizedIDCell = ({ video }: { video: UnfurlItem }) => {
  return <span className={css.idCell}>{video.id}</span>;
};
MemoizedIDCell.displayName = 'MemoizedIDCell';

const MemoizedDescriptionCell = ({ video }: { video: UnfurlItem }) => {
  return <span className={css.descriptionCell}>{video.description}</span>;
};
MemoizedDescriptionCell.displayName = 'MemoizedDescriptionCell';

// Add this new component after MemoizedDescriptionCell
const MemoizedShortsCell = ({ video }: { video: UnfurlItem }) => {
  if (!('isShorts' in video) || video.isShorts === undefined) {
    return <span className={css.shortsCell}>—</span>;
  }
  return <span className={css.shortsCell}>{video.isShorts ? 'Yes' : 'No'}</span>;
};
MemoizedShortsCell.displayName = 'MemoizedShortsCell';

// Ref interface to expose DataGrid selection and filtering methods to parent
export interface MediaDataGridRef {
  getSelection: () => string[];
  getFullSelection: () => string[]; // Includes duplicates that are visually selected
  getVisibleSelectedDuplicatesCount: () => number; // Count of visible selected duplicates
  setSelection: (selection: string[]) => void;
  clearSelection: () => void;
  setFilterModel: (filterModel: GridFilterModel) => void;
  getFilterModel: () => GridFilterModel;
  clearFilters: () => void;
  getFilteredOutCount: (selectedIds: string[]) => number;
  isReady: () => boolean;
}

interface MediaDataTableps {
  data: UnfurlItem[];
  onSelectionChange?: (selectedIds: string[]) => void;
  selectedIds?: string[];
  existingUniqueIds: Record<string, string>;
  filterModel?: GridFilterModel;
  onFilterModelChange?: (filterModel: GridFilterModel) => void;
  onFilteredOutCountChange?: (count: number) => void;
  onVisibleSelectedDuplicatesCountChange?: (count: number) => void;
}

type TimePeriodFilter = 'lastWeek' | 'lastMonth' | 'lastYear' | null;

const DEFAULT_SORT_OPTION: SortOption = 'newest';

const MediaDataGridInner = forwardRef<MediaDataGridRef, MediaDataTableps>(
  (
    {
      data,
      onSelectionChange,
      selectedIds = [],
      existingUniqueIds,
      filterModel,
      onFilterModelChange,
      onFilteredOutCountChange,
      onVisibleSelectedDuplicatesCountChange,
    },
    ref,
  ) => {
    const uploadURLStore = useUploadURL();

    const [thumbnailErrors, setThumbnailErrors] = useState<Set<string>>(new Set());
    const [filtersAnchorEl, setFiltersAnchorEl] = useState<HTMLButtonElement | null>(null);
    const isFiltersPopoverOpen = Boolean(filtersAnchorEl);
    const [sortOption, setSortOption] = useState<SortOption>(DEFAULT_SORT_OPTION);
    const [showInLibrary, setShowInLibrary] = useState(true);
    const [showShorts, setShowShorts] = useState(true);
    const [showLongForm, setShowLongForm] = useState(true);
    // The Shorts / Long-form (Format) filter is YouTube-specific; only show it
    // when the collection actually contains YouTube videos (e.g. hidden for
    // Google Drive folders, which hold images/audio/video).
    const hasYoutubeContent = useMemo(
      () => data.some((item) => item.extractor === 'youtube'),
      [data],
    );
    // Each pasted source describes its own fields. The ones no hand-built column covers get a
    // column of their own, so whatever the backend sends shows up without a frontend change.
    const columnDefinitions = useMemo(() => mergeColumnDefinitions(data), [data]);
    const unfurlColumns = useMemo(
      () =>
        columnDefinitions
          .filter((definition) => !CURATED_UNFURL_KEYS.has(definition.key))
          .map(buildUnfurlColumn),
      [columnDefinitions],
    );
    // Columns start as the backend asks: shown unless it marks them hidden, and a hand-built
    // column is hidden when no source sends its field (Facebook ads have no duration). Only the
    // columns the user toggles are kept as overrides, so adding a link through "Edit URLs" still
    // brings its own fields in with their defaults.
    const defaultColumnVisibility = useMemo((): GridColumnVisibilityModel => {
      const model: GridColumnVisibilityModel = {
        duration: isDeclaredVisible(columnDefinitions, 'duration', true),
        isShorts: isDeclaredVisible(columnDefinitions, 'isShorts', true),
        publishedAt: isDeclaredVisible(columnDefinitions, 'publishedAt', true),
        description: isDeclaredVisible(columnDefinitions, 'description', false),
        inLibrary: false,
        url: false,
        id: false,
        status: false,
      };
      for (const definition of columnDefinitions) {
        if (CURATED_UNFURL_KEYS.has(definition.key)) continue;
        model[unfurlColumnField(definition.key)] = !definition.defaultHidden;
      }
      return model;
    }, [columnDefinitions]);
    const [columnVisibilityOverrides, setColumnVisibilityOverrides] =
      useState<GridColumnVisibilityModel>({});
    const columnVisibilityModel = useMemo(
      () => ({ ...defaultColumnVisibility, ...columnVisibilityOverrides }),
      [defaultColumnVisibility, columnVisibilityOverrides],
    );
    const handleColumnVisibilityModelChange = useCallback(
      (next: GridColumnVisibilityModel) =>
        setColumnVisibilityOverrides((overrides) =>
          applyVisibilityChange(columnVisibilityModel, overrides, next),
        ),
      [columnVisibilityModel],
    );
    const [titleContainsChips, setTitleContainsChips] = useState<string[]>([]);
    const [titleContainsInput, setTitleContainsInput] = useState('');
    const [titleFilterMode, setTitleFilterMode] = useState<'any' | 'all'>('any');
    const titleInputRef = useRef<HTMLInputElement>(null);
    const titleChipsContainerRef = useRef<HTMLDivElement>(null);
    const apiRef = useGridApiRef();
    const skipNextSelectionChange = useRef(false);

    // Status polling is folder-level — one request regardless of how many rows
    // are on screen, so there is no visible-row set to compute or match back to
    // media IDs any more.
    useMediaStatusPolling();
    // Internal filter model state if not controlled by parent
    const [internalFilterModel, setInternalFilterModel] = useState<GridFilterModel>({
      items: [],
    });

    // Use controlled filterModel if provided, otherwise use internal state
    const currentFilterModel = useMemo(
      () => (filterModel !== undefined ? filterModel : internalFilterModel),
      [filterModel, internalFilterModel],
    );

    // Use useCallback to stabilize the handler reference
    const handleFilterModelChange = useCallback(
      (newFilterModel: GridFilterModel) => {
        if (onFilterModelChange) {
          onFilterModelChange(newFilterModel);
        } else {
          setInternalFilterModel(newFilterModel);
        }
      },
      [onFilterModelChange],
    );

    // Filter data based on title contains chips + current input (any/all logic)
    const filteredByTitleData = useMemo(() => {
      // Combine chips with current input (if not empty)
      const allFilters = titleContainsInput.trim()
        ? [...titleContainsChips, titleContainsInput.trim()]
        : titleContainsChips;

      if (allFilters.length === 0) return data;

      // Use "some" for "any of" mode, "every" for "all of" mode
      const matchFn = titleFilterMode === 'any' ? 'some' : 'every';
      return data.filter((item) =>
        allFilters[matchFn]((filter) => item.title.toLowerCase().includes(filter.toLowerCase())),
      );
    }, [data, titleContainsChips, titleContainsInput, titleFilterMode]);

    // Filter by format (Shorts/Long form) - applied after title filtering
    const filteredData = useMemo(() => {
      // If both format checkboxes unchecked, show nothing
      if (!showShorts && !showLongForm) {
        return [];
      }
      // If both checked, show all (filtered by title)
      if (showShorts && showLongForm) {
        return filteredByTitleData;
      }
      // Filter based on checkbox state
      return filteredByTitleData.filter((item) => {
        // If isShorts is not present or undefined, treat as long-form
        if (!('isShorts' in item) || item.isShorts === undefined) {
          return showLongForm;
        }
        if (showShorts && !showLongForm) {
          return item.isShorts === true;
        }
        if (!showShorts && showLongForm) {
          return item.isShorts === false;
        }
        return true;
      });
    }, [filteredByTitleData, showShorts, showLongForm]);

    // Compute status for each row (enriched with a `status` field for DataTable)
    const percentileDuration = useMemo(() => computeWarningPercentileDuration(data), [data]);

    // All three deps are reference-stable while the user browses
    // (existingUniqueIds is a snapshot frozen at flow open — see
    // useUrlUploadFlow), so this returns the SAME array across re-renders.
    // That stability is load-bearing: DataTable re-renders every visible
    // cell whenever the rows ARRAY identity changes, even with identical
    // content — the redraw/scroll-reset bug.
    const rowsWithStatus = useMemo(
      () =>
        filteredData.map((item) => ({
          ...item,
          status: computeMediaStatus(item, percentileDuration, existingUniqueIds),
        })),
      [filteredData, percentileDuration, existingUniqueIds],
    );

    // Get duplicate checking function
    const { isDuplicate } = useDuplicateChecker({
      data: filteredData,
      existingUniqueIds,
    });

    // Compute error IDs from rows with status === 'error'
    const errorIds = useMemo(
      () => rowsWithStatus.filter((row) => row.status === 'error').map((row) => row.id),
      [rowsWithStatus],
    );

    // Use the custom hook for DataGrid selection logic
    const {
      duplicateIds,
      isRowSelectable,
      getRowClassName,
      handleSelectionChange,
      rowSelectionModel,
      getFooterRowSelectedText,
    } = useDataGridSelection({
      data: filteredData,
      existingUniqueIds,
      onSelectionChange: onSelectionChange || (() => {}),
      selectedIds,
      errorIds,
    });

    // Memoized selection change handler to avoid recreating on every render
    const onRowSelectionModelChange = useCallback(
      (newSelection: any) => {
        // Skip if we're programmatically setting the selection
        if (skipNextSelectionChange.current) {
          skipNextSelectionChange.current = false;
          return;
        }
        handleSelectionChange(newSelection);
      },
      [handleSelectionChange],
    );

    // Helper function to calculate filtered out count
    const calculateFilteredOutCount = useCallback(
      (selectedIdsToCheck: string[]): number => {
        try {
          if (!apiRef.current) return 0;

          // Get currently visible/filtered row IDs from DataGrid
          const visibleRowIds = gridFilteredSortedRowIdsSelector(apiRef) as (string | number)[];
          const visibleRowIdsSet = new Set(visibleRowIds.map((id) => String(id)));

          // Count how many selected IDs are not in the visible set
          return selectedIdsToCheck.filter(
            (selectedId) => !visibleRowIdsSet.has(String(selectedId)),
          ).length;
        } catch (error) {
          console.error('Failed to calculate filtered out count:', error);
          return 0;
        }
      },
      [apiRef],
    );

    // Expose methods to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        getSelection: () => {
          try {
            if (apiRef.current?.getSelectedRows) {
              const selectedRows = apiRef.current.getSelectedRows();
              const selectedIds = Array.from(selectedRows.keys()).map(String);
              // Filter out duplicates from reported selection using shared utility
              return filterNonDuplicates(selectedIds, data, (item) =>
                isMediaDuplicate(item, existingUniqueIds),
              );
            }
            return [];
          } catch (error) {
            console.error('Failed to get selection from DataGrid:', error);
            return [];
          }
        },
        getFullSelection: () => {
          try {
            if (apiRef.current?.getSelectedRows) {
              const selectedRows = apiRef.current.getSelectedRows();
              // Return all selected IDs including duplicates
              return Array.from(selectedRows.keys()).map(String);
            }
            return [];
          } catch (error) {
            console.error('Failed to get full selection from DataGrid:', error);
            return [];
          }
        },
        getVisibleSelectedDuplicatesCount: () => {
          try {
            if (!apiRef.current) return 0;

            // Get currently visible row IDs (not filtered out)
            const visibleRowIds = gridFilteredSortedRowIdsSelector(apiRef) as (string | number)[];
            const visibleRowIdsSet = new Set(visibleRowIds.map((id) => String(id)));

            // Get all selected rows (including duplicates)
            const selectedRows = apiRef.current.getSelectedRows();
            const allSelectedIds = Array.from(selectedRows.keys()).map(String);

            // Filter to only visible selected items
            const visibleSelectedIds = allSelectedIds.filter((id) =>
              visibleRowIdsSet.has(String(id)),
            );

            // Count how many of the visible selected items are duplicates
            return visibleSelectedIds.filter((id) => {
              const item = data.find((item) => item.id === id);
              return item ? isMediaDuplicate(item, existingUniqueIds) : false;
            }).length;
          } catch (error) {
            console.error('Failed to get visible selected duplicates count:', error);
            return 0;
          }
        },
        setSelection: (selection: string[]) => {
          try {
            if (apiRef.current?.setRowSelectionModel) {
              // Skip the next selection change callback to prevent reset
              skipNextSelectionChange.current = true;
              // Include duplicates in visual selection but they won't be selectable
              const visualSelection = [...selection, ...duplicateIds];
              apiRef.current.setRowSelectionModel(visualSelection as any);
            }
          } catch (error) {
            console.error('Failed to set selection in DataGrid:', error);
          }
        },
        clearSelection: () => {
          try {
            if (apiRef.current?.setRowSelectionModel) {
              apiRef.current.setRowSelectionModel([] as any);
            }
          } catch (error) {
            console.error('Failed to clear selection in DataGrid:', error);
          }
        },
        setFilterModel: (filterModel: GridFilterModel) => {
          try {
            if (apiRef.current?.setFilterModel) {
              apiRef.current.setFilterModel(filterModel);
            }
          } catch (error) {
            console.error('Failed to set filter model in DataGrid:', error);
          }
        },
        getFilterModel: () => {
          // Return the current filterModel
          return currentFilterModel;
        },
        clearFilters: () => {
          try {
            if (apiRef.current?.setFilterModel) {
              apiRef.current.setFilterModel({ items: [] });
            }
          } catch (error) {
            console.error('Failed to clear filters in DataGrid:', error);
          }
        },
        getFilteredOutCount: (selectedIdsToCheck: string[]) => {
          return calculateFilteredOutCount(selectedIdsToCheck);
        },
        isReady: () => {
          return apiRef.current?.setRowSelectionModel !== undefined;
        },
      }),
      [existingUniqueIds, data, duplicateIds, currentFilterModel, calculateFilteredOutCount],
    );

    // Notify parent of filtered out count when filters or selection changes
    useEffect(() => {
      if (!apiRef.current || !onFilteredOutCountChange) return;

      const updateFilteredOutCount = () => {
        try {
          const count = calculateFilteredOutCount(selectedIds);
          onFilteredOutCountChange(count);
        } catch (error) {
          console.error('Failed to update filtered out count:', error);
          onFilteredOutCountChange(0);
        }
      };

      // Initial calculation
      updateFilteredOutCount();

      // Subscribe to specific events (filter and sort changes affect visible rows)
      const unsubscribeFilter = apiRef.current?.subscribeEvent(
        'filterModelChange',
        updateFilteredOutCount,
      );
      const unsubscribeSort = apiRef.current?.subscribeEvent(
        'sortModelChange',
        updateFilteredOutCount,
      );

      return () => {
        if (unsubscribeFilter) unsubscribeFilter();
        if (unsubscribeSort) unsubscribeSort();
      };
    }, [
      apiRef,
      selectedIds,
      data.length,
      onFilteredOutCountChange,
      calculateFilteredOutCount,
      filteredData.length,
    ]);

    // Notify parent of visible selected duplicates count when filters or selection changes
    useEffect(() => {
      if (!apiRef.current || !onVisibleSelectedDuplicatesCountChange) return;

      const updateVisibleSelectedDuplicatesCount = () => {
        try {
          if (!apiRef.current) return;

          // Get currently visible row IDs (not filtered out)
          const visibleRowIds = gridFilteredSortedRowIdsSelector(apiRef) as (string | number)[];
          const visibleRowIdsSet = new Set(visibleRowIds.map((id) => String(id)));

          // Get all selected rows (including duplicates)
          const selectedRows = apiRef.current.getSelectedRows();
          const allSelectedIds = Array.from(selectedRows.keys()).map(String);

          // Filter to only visible selected items
          const visibleSelectedIds = allSelectedIds.filter((id) =>
            visibleRowIdsSet.has(String(id)),
          );

          // Count how many of the visible selected items are duplicates
          const count = visibleSelectedIds.filter((id) => {
            const item = data.find((item) => item.id === id);
            return item ? isMediaDuplicate(item, existingUniqueIds) : false;
          }).length;

          onVisibleSelectedDuplicatesCountChange(count);
        } catch (error) {
          console.error('Failed to update visible selected duplicates count:', error);
          onVisibleSelectedDuplicatesCountChange(0);
        }
      };

      // Initial calculation
      updateVisibleSelectedDuplicatesCount();

      // Subscribe to specific events (filter, sort, and selection changes)
      const unsubscribeFilter = apiRef.current?.subscribeEvent?.(
        'filterModelChange',
        updateVisibleSelectedDuplicatesCount,
      );
      const unsubscribeSort = apiRef.current?.subscribeEvent?.(
        'sortModelChange',
        updateVisibleSelectedDuplicatesCount,
      );
      const unsubscribeSelection = apiRef.current?.subscribeEvent?.(
        'rowSelectionChange',
        updateVisibleSelectedDuplicatesCount,
      );

      return () => {
        if (unsubscribeFilter) unsubscribeFilter();
        if (unsubscribeSort) unsubscribeSort();
        if (unsubscribeSelection) unsubscribeSelection();
      };
    }, [
      apiRef,
      selectedIds,
      data,
      existingUniqueIds,
      onVisibleSelectedDuplicatesCountChange,
      filteredData.length,
    ]);

    const handleThumbnailError = useCallback((itemId: string) => {
      setThumbnailErrors((prev) => new Set(prev).add(itemId));
    }, []);

    // Calculate start date for time period filters
    const getStartDate = useCallback((period: TimePeriodFilter): Date | null => {
      if (!period) return null;
      const now = new Date();
      const start = new Date(now);

      switch (period) {
        case 'lastWeek':
          start.setDate(now.getDate() - 7);
          break;
        case 'lastMonth':
          start.setMonth(now.getMonth() - 1);
          break;
        case 'lastYear':
          start.setFullYear(now.getFullYear() - 1);
          break;
        default:
          return null;
      }

      return start;
    }, []);

    // Check if current filter model matches each chip's filter
    // This determines if chips should be active (pink) or inactive (gray)
    const isTimePeriodFilterActive = useCallback(
      (period: TimePeriodFilter): boolean => {
        if (!period) return false;
        const publishedAtFilter = currentFilterModel?.items?.find(
          (item) => item.field === 'publishedAt' && item.operator === 'onOrAfter',
        );

        if (!publishedAtFilter || !publishedAtFilter.value) {
          return false;
        }

        const filterDate = new Date(publishedAtFilter.value);
        const expectedDate = getStartDate(period);
        if (!expectedDate) return false;

        // Compare dates (within 24 hours to account for time differences)
        return Math.abs(filterDate.getTime() - expectedDate.getTime()) < 24 * 60 * 60 * 1000;
      },
      [currentFilterModel, getStartDate],
    );

    // Check if "Any time" is active (no publishedAt filter)
    const isAnyTimeActive = useCallback((): boolean => {
      const publishedAtFilter = currentFilterModel?.items?.find(
        (item) => item.field === 'publishedAt' && item.operator === 'onOrAfter',
      );
      return !publishedAtFilter;
    }, [currentFilterModel]);

    // Handle time period chip click - directly update filter model
    const handleTimePeriodClick = useCallback(
      (period: TimePeriodFilter) => {
        const currentPeriod = isTimePeriodFilterActive(period) ? null : period;

        const filterItems: GridFilterModel['items'] = [];

        // Add time period filter if not toggling off
        if (currentPeriod) {
          const startDate = getStartDate(currentPeriod);
          if (startDate) {
            filterItems.push({
              field: 'publishedAt',
              operator: 'onOrAfter',
              value: startDate,
            });
          }
        }

        // Get existing filters (excluding publishedAt that we're managing)
        const existingItems = (currentFilterModel?.items || []).filter(
          (item) => item.field !== 'publishedAt',
        );

        // Create new filter model
        const newFilterModel: GridFilterModel = {
          items: [...existingItems, ...filterItems],
        };

        handleFilterModelChange(newFilterModel);
      },
      [currentFilterModel, handleFilterModelChange, getStartDate, isTimePeriodFilterActive],
    );

    // Handle "Any time" chip click - removes publishedAt filter
    const handleAnyTimeClick = useCallback(() => {
      // Get existing filters (excluding publishedAt that we're managing)
      const existingItems = (currentFilterModel?.items || []).filter(
        (item) => item.field !== 'publishedAt',
      );

      // Create new filter model (publishedAt filter is excluded)
      const newFilterModel: GridFilterModel = {
        items: existingItems,
      };

      handleFilterModelChange(newFilterModel);
    }, [currentFilterModel, handleFilterModelChange]);

    // Handle show in-library toggle - updates filter model
    const handleShowInLibraryChange = useCallback(
      (checked: boolean) => {
        setShowInLibrary(checked);

        const filterItems: GridFilterModel['items'] = [];

        // Get existing filters (excluding inLibrary that we're managing)
        const existingItems = (currentFilterModel?.items || []).filter(
          (item) => item.field !== 'inLibrary',
        );

        // If unchecked, add filter to hide in-library items
        if (!checked) {
          filterItems.push({
            field: 'inLibrary',
            operator: 'is',
            value: false,
          });
        }

        // Create new filter model
        const newFilterModel: GridFilterModel = {
          items: [...existingItems, ...filterItems],
        };

        handleFilterModelChange(newFilterModel);
      },
      [currentFilterModel, handleFilterModelChange],
    );

    // Handle format checkbox changes (Shorts / Long form)
    // Note: Filtering is now handled by the filteredData useMemo, not by DataGrid filter model
    const handleFormatChange = useCallback((type: 'shorts' | 'longForm', checked: boolean) => {
      if (type === 'shorts') {
        setShowShorts(checked);
      } else {
        setShowLongForm(checked);
      }
    }, []);

    // Handle adding a chip when comma is pressed
    const handleTitleInputKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === ',' || e.key === 'Enter') {
          e.preventDefault();
          const value = titleContainsInput.trim();
          if (value && !titleContainsChips.includes(value)) {
            setTitleContainsChips([...titleContainsChips, value]);
            setTitleContainsInput('');
          }
        } else if (
          e.key === 'Backspace' &&
          titleContainsInput === '' &&
          titleContainsChips.length > 0
        ) {
          // Delete last chip when backspace is pressed on empty input
          setTitleContainsChips(titleContainsChips.slice(0, -1));
        }
      },
      [titleContainsInput, titleContainsChips],
    );

    // A pasted block of lines (a list of names) becomes a chip per line. A single line pastes as
    // usual — a text input would otherwise drop the line breaks and run the names together.
    const handleTitleInputPaste = useCallback(
      (e: React.ClipboardEvent<HTMLInputElement>) => {
        const lines = e.clipboardData.getData('text').split(/\r?\n/);
        if (lines.length < 2) return;
        e.preventDefault();
        const added = lines.map((line) => line.trim()).filter(Boolean);
        setTitleContainsChips([...new Set([...titleContainsChips, ...added])]);
      },
      [titleContainsChips],
    );

    // Handle blur - save current input as chip when clicking away
    const handleTitleInputBlur = useCallback(() => {
      const value = titleContainsInput.trim();
      if (value && !titleContainsChips.includes(value)) {
        setTitleContainsChips([...titleContainsChips, value]);
        setTitleContainsInput('');
      }
    }, [titleContainsInput, titleContainsChips]);

    // Handle removing a chip
    const handleDeleteTitleChip = useCallback(
      (chipToDelete: string) => {
        setTitleContainsChips(titleContainsChips.filter((chip) => chip !== chipToDelete));
      },
      [titleContainsChips],
    );

    // Handle clear filters
    const handleClearFilters = useCallback(() => {
      // Clear all filters and reset to defaults
      trackingStore.chooseMediaFiltersReset();
      setShowInLibrary(true);
      setShowShorts(true);
      setShowLongForm(true);
      setTitleContainsChips([]);
      setTitleContainsInput('');
      setTitleFilterMode('any');
      handleFilterModelChange({ items: [] });
    }, [handleFilterModelChange]);

    // Auto-scroll title chips container into view when chips are added
    useEffect(() => {
      if (titleChipsContainerRef.current && titleContainsChips.length > 0) {
        titleChipsContainerRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    }, [titleContainsChips.length]);

    // Calculate active filter count
    const activeFilterCount = useMemo(() => {
      let count = 0;
      // Count time period filter (if not "any time")
      if (!isAnyTimeActive()) {
        count += 1;
      }
      // Count format filter (if not both checked)
      if (!showShorts || !showLongForm) {
        count += 1;
      }
      // Count show in-library filter (if turned off)
      if (!showInLibrary) {
        count += 1;
      }
      // Count title contains filter (if any chips)
      if (titleContainsChips.length > 0) {
        count += 1;
      }
      return count;
    }, [isAnyTimeActive, showShorts, showLongForm, showInLibrary, titleContainsChips]);

    // Compose filterModel: content filters + status filters from store
    const composedFilterModel = useMemo((): GridFilterModel => {
      const contentItems = currentFilterModel?.items ?? [];
      const activeStatuses = Array.from(uploadURLStore.activeStatusFilters);

      if (activeStatuses.length === 0) {
        return { items: contentItems };
      }

      // Add status filter: show only rows matching any active status
      const statusFilterItem = {
        field: 'status',
        operator: 'isAnyOf',
        value: activeStatuses,
      };

      return { items: [...contentItems, statusFilterItem] };
    }, [currentFilterModel, uploadURLStore.activeStatusFilters]);

    // "Show all" handler — clears all status filter toggles
    const handleShowAll = useCallback(() => {
      uploadURLStore.clearStatusFilters();
    }, [uploadURLStore]);

    // Popover handlers
    const handleFiltersButtonClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
      setFiltersAnchorEl(event.currentTarget);
    }, []);

    const handleFiltersPopoverClose = useCallback(() => {
      setFiltersAnchorEl(null);
    }, []);

    // Handle sort option change
    const handleSortChange = useCallback(
      (event: SelectChangeEvent<SortOption>) => {
        const newSortOption = event.target.value as SortOption;
        setSortOption(newSortOption);
        trackingStore.chooseMediaSortApplied({ sortBy: newSortOption });

        const sortConfig = SORT_OPTIONS[newSortOption];
        const newSortModel: GridSortModel = [{ field: sortConfig.field, sort: sortConfig.sort }];

        if (apiRef.current?.setSortModel) {
          apiRef.current.setSortModel(newSortModel);
        }
      },
      [apiRef],
    );

    // Combine chips with current input for highlighting
    const highlightTerms = useMemo(() => {
      const terms = [...titleContainsChips];
      if (titleContainsInput.trim()) {
        terms.push(titleContainsInput.trim());
      }
      return terms;
    }, [titleContainsChips, titleContainsInput]);

    // Memoized render functions that create stable references
    const renderTitleWithThumbnail = useCallback(
      (params: GridRenderCellParams) => {
        const video = params.row as UnfurlItem;
        const status = (params.row as UnfurlItem & { status: MediaStatusType | null }).status;
        const hasThumbnailError = thumbnailErrors.has(video.id);

        return (
          <MemoizedTitleCell
            video={video}
            status={status}
            hasThumbnailError={hasThumbnailError}
            onThumbnailError={handleThumbnailError}
            highlightTerms={highlightTerms}
          />
        );
      },
      [thumbnailErrors, handleThumbnailError, highlightTerms],
    );

    const renderDuration = useCallback((params: GridRenderCellParams) => {
      const video = params.row as UnfurlItem;
      return <MemoizedDurationCell video={video} />;
    }, []);

    const renderPublishedDate = useCallback((params: GridRenderCellParams) => {
      const video = params.row as UnfurlItem;
      return <MemoizedPublishedDateCell video={video} />;
    }, []);

    const renderInLibrary = useCallback(
      (params: GridRenderCellParams) => {
        const video = params.row as UnfurlItem;
        return <MemoizedInLibraryCell video={video} isDuplicate={isDuplicate} />;
      },
      [isDuplicate],
    );

    const renderURLCell = useCallback((params: GridRenderCellParams) => {
      const video = params.row as UnfurlItem;
      return <MemoizedURLCell video={video} />;
    }, []);

    const renderIDCell = useCallback((params: GridRenderCellParams) => {
      const video = params.row as UnfurlItem;
      return <MemoizedIDCell video={video} />;
    }, []);

    const renderDescriptionCell = useCallback((params: GridRenderCellParams) => {
      const video = params.row as UnfurlItem;
      return <MemoizedDescriptionCell video={video} />;
    }, []);

    // Add this new render function after renderDescriptionCell
    const renderShortsCell = useCallback((params: GridRenderCellParams) => {
      const video = params.row as UnfurlItem;
      return <MemoizedShortsCell video={video} />;
    }, []);

    // Memoize columns to prevent recreation on every render
    const columns: GridColDef[] = useMemo(
      () => [
        {
          ...GRID_CHECKBOX_SELECTION_COL_DEF,
          renderCell: (params: GridRenderCellParams) => {
            const status = (params.row as UnfurlItem & { status: MediaStatusType | null }).status;

            if (status === 'error' || status === 'in-library') {
              const color =
                status === 'error' ? 'rgba(214, 69, 80, 0.8)' : 'rgba(208, 169, 239, 0.8)';
              return (
                <Tooltip title={getStatusTooltip(status)} arrow placement="top">
                  <LockOutlinedIcon sx={{ fontSize: 18, color: `${color} !important` }} />
                </Tooltip>
              );
            }

            // Default checkbox for normal/warning rows
            const defaultRenderCell = GRID_CHECKBOX_SELECTION_COL_DEF.renderCell;
            if (defaultRenderCell) {
              return defaultRenderCell(params as any);
            }
            return null;
          },
        } as GridColDef,
        {
          field: 'title',
          headerName: 'Media name',
          flex: 1,
          minWidth: 300,
          renderCell: renderTitleWithThumbnail,
          sortable: true,
          filterable: true,
          type: 'string',
        },
        {
          field: 'duration',
          headerName: 'Duration',
          flex: 0.4,
          minWidth: headerMinWidth('Duration'),
          renderCell: renderDuration,
          sortable: true,
          filterable: true,
          type: 'number',
          align: 'center',
          headerAlign: 'center',
          valueGetter: (value, row) => {
            const video = row as UnfurlItem;
            return video.duration || 0;
          },
          filterOperators: getGridNumericOperators()
            .filter((operator) => operator.value !== 'isAnyOf')
            .map((operator) => ({
              ...operator,
              InputComponent: operator.InputComponent ? DurationFilterInput : undefined,
            })),
        },
        // "YT Short?" is YouTube-specific — only show it when the collection
        // contains YouTube videos (hidden for e.g. Google Drive folders).
        ...(hasYoutubeContent
          ? [
              {
                field: 'isShorts',
                headerName: 'YT Short?',
                flex: 0.2,
                minWidth: headerMinWidth('YT Short?'),
                renderCell: renderShortsCell,
                sortable: true,
                filterable: true,
                type: 'singleSelect', // Change from 'boolean' to 'singleSelect'
                align: 'center',
                headerAlign: 'center',
                valueGetter: (value, row) => {
                  const video = row as UnfurlItem;
                  // Return undefined if isShorts is not present or undefined, otherwise return boolean
                  if (!('isShorts' in video) || video.isShorts === undefined) {
                    return undefined;
                  }
                  return video.isShorts === true;
                },
                valueOptions: [
                  { value: true, label: 'Yes' },
                  { value: false, label: 'No' },
                ],
                valueFormatter: (value: boolean | undefined) => {
                  if (value === true) return 'Yes';
                  if (value === false) return 'No';
                  return '—';
                },
                filterOperators: getGridSingleSelectOperators().filter(
                  (operator) => operator.value === 'is',
                ),
              } as GridColDef,
            ]
          : []),
        {
          field: 'publishedAt',
          headerName: 'Published',
          flex: 0.4,
          minWidth: headerMinWidth('Published'),
          renderCell: renderPublishedDate,
          sortable: true,
          filterable: true,
          type: 'date',
          align: 'center',
          headerAlign: 'center',
          valueGetter: (value, row) => {
            const video = row as UnfurlItem;
            return 'publishedAt' in video && video.publishedAt ? new Date(video.publishedAt) : null;
          },
          // Custom sort comparator to put N/A (null) values last regardless of sort direction
          sortComparator: (v1, v2, param1, param2) => {
            const isAsc = param1.api.getSortModel()[0]?.sort === 'asc';
            if (v1 === null && v2 === null) return 0;
            if (v1 === null) return isAsc ? 1 : -1;
            if (v2 === null) return isAsc ? -1 : 1;
            return (v1 as Date).getTime() - (v2 as Date).getTime();
          },
        },
        ...unfurlColumns,
        {
          field: 'description',
          headerName: 'Description',
          flex: 0.8,
          minWidth: 200,
          renderCell: renderDescriptionCell,
          filterable: true,
          type: 'string',
          align: 'left',
          headerAlign: 'center',
          valueGetter: (value, row) => {
            const video = row as UnfurlItem;
            return video.description;
          },
        },
        {
          field: 'url',
          headerName: 'URL',
          flex: 0.4,
          minWidth: 300,
          renderCell: renderURLCell,
          sortable: true,
          filterable: true,
          type: 'string',
          align: 'center',
          headerAlign: 'center',
          valueGetter: (value, row) => {
            const video = row as UnfurlItem;
            return video.url;
          },
        },
        {
          field: 'id',
          headerName: 'ID',
          flex: 0.4,
          minWidth: 140,
          renderCell: renderIDCell,
          sortable: true,
          filterable: true,
          type: 'string',
          align: 'center',
          headerAlign: 'center',
          valueGetter: (value, row) => {
            const video = row as UnfurlItem;
            return video.id;
          },
        },
        {
          field: 'inLibrary',
          headerName: 'In library?',
          flex: 0.3,
          minWidth: 120,
          renderCell: renderInLibrary,
          sortable: true,
          filterable: true,
          type: 'boolean',
          align: 'center',
          headerAlign: 'center',
          valueGetter: (value, row) => {
            const video = row as UnfurlItem;
            return isDuplicate(video);
          },
        },
        {
          field: 'status',
          headerName: 'Status',
          type: 'singleSelect',
          valueOptions: ['error', 'warning', 'in-library'] as MediaStatusType[],
          filterable: true,
          sortable: false,
          valueGetter: (value, row) => {
            return (row as UnfurlItem & { status: MediaStatusType | null }).status ?? '';
          },
          filterOperators: getGridSingleSelectOperators().filter(
            (op) => op.value === 'isAnyOf' || op.value === 'is',
          ),
        },
      ],
      [
        renderTitleWithThumbnail,
        renderDuration,
        renderPublishedDate,
        renderInLibrary,
        isDuplicate,
        renderDescriptionCell,
        renderURLCell,
        renderIDCell,
        renderShortsCell,
        hasYoutubeContent,
        unfurlColumns,
      ],
    );

    // Apply content filter items (time period, duration, in-library, any column's own filter) to
    // rowsWithStatus so that header chip counts reflect DataTable content filters, not just
    // title/format pre-filtering. Status filter items are excluded — chips always show totals for
    // the content-filtered set.
    //
    // DataTable doesn't expose an intermediate "content-filtered only" state (before status
    // filters), so this runs the table's own row filter over the content items. That keeps the
    // counts in step with the rows on screen for every column, including the ones built from the
    // backend's definitions.
    const contentFilteredRows = useMemo(() => {
      const items = currentFilterModel?.items ?? [];
      if (items.length === 0) return rowsWithStatus;
      return rowsWithStatus.filter(createRowFilter(columns, { ...currentFilterModel, items }));
    }, [rowsWithStatus, currentFilterModel, columns]);

    // Compute header messages from content-filtered rows (after all content filters, before status filters)
    const headerMessages = useMemo((): HeaderMessage[] => {
      const counts: Record<MediaStatusType, number> = {
        error: 0,
        warning: 0,
        'in-library': 0,
      };

      for (const row of contentFilteredRows) {
        if (row.status) {
          counts[row.status]++;
        }
      }

      const messages: HeaderMessage[] = [];
      if (counts.error > 0) {
        messages.push({
          id: 'status-error',
          type: 'error',
          text: counts.error === 1 ? '1 error' : `${counts.error} errors`,
        });
      }
      if (counts.warning > 0) {
        messages.push({
          id: 'status-warning',
          type: 'warning',
          text: counts.warning === 1 ? '1 warning' : `${counts.warning} warnings`,
        });
      }
      if (counts['in-library'] > 0) {
        messages.push({
          id: 'status-in-library',
          type: 'in-library',
          text:
            counts['in-library'] === 1
              ? '1 already in library'
              : `${counts['in-library']} already in library`,
        });
      }

      return messages;
    }, [contentFilteredRows]);

    // Determine the current time period selection for radio group
    const getCurrentTimePeriod = (): string => {
      if (isTimePeriodFilterActive('lastWeek')) return 'lastWeek';
      if (isTimePeriodFilterActive('lastMonth')) return 'lastMonth';
      if (isTimePeriodFilterActive('lastYear')) return 'lastYear';
      return 'anyTime';
    };

    return (
      <div className={css.dataGridContainer}>
        <MediaDataGridHeader
          activeFilterCount={activeFilterCount}
          isFiltersPopoverOpen={isFiltersPopoverOpen}
          onFiltersButtonClick={handleFiltersButtonClick}
          sortOption={sortOption}
          onSortChange={handleSortChange}
          headerMessages={headerMessages}
          onShowAll={handleShowAll}
        />
        <MediaDataGridFilterPopover
          isOpen={isFiltersPopoverOpen}
          anchorEl={filtersAnchorEl}
          onClose={handleFiltersPopoverClose}
          getCurrentTimePeriod={getCurrentTimePeriod}
          onTimePeriodClick={handleTimePeriodClick}
          onAnyTimeClick={handleAnyTimeClick}
          showFormatFilter={hasYoutubeContent}
          showShorts={showShorts}
          showLongForm={showLongForm}
          onFormatChange={handleFormatChange}
          showInLibrary={showInLibrary}
          onShowInLibraryChange={handleShowInLibraryChange}
          titleContainsChips={titleContainsChips}
          titleContainsInput={titleContainsInput}
          titleFilterMode={titleFilterMode}
          onTitleContainsInputChange={setTitleContainsInput}
          onTitleFilterModeChange={() =>
            setTitleFilterMode(titleFilterMode === 'any' ? 'all' : 'any')
          }
          onTitleInputKeyDown={handleTitleInputKeyDown}
          onTitleInputPaste={handleTitleInputPaste}
          onTitleInputBlur={handleTitleInputBlur}
          onDeleteTitleChip={handleDeleteTitleChip}
          titleInputRef={titleInputRef}
          titleChipsContainerRef={titleChipsContainerRef}
          onClearFilters={handleClearFilters}
        />
        <DataTable
          apiRef={apiRef}
          searchField="title"
          rows={rowsWithStatus}
          columns={columns}
          className={css.dataGrid}
          rowHeight={55}
          sortingOrder={['asc', 'desc']}
          filterModel={composedFilterModel}
          onFilterModelChange={(newModel) => {
            // Strip status filter items before storing — those are managed by the store
            const contentOnly: GridFilterModel = {
              ...newModel,
              items: newModel.items.filter((item) => item.field !== 'status'),
            };
            handleFilterModelChange(contentOnly);
          }}
          checkboxSelection
          isRowSelectable={isRowSelectable}
          getRowId={(row) => (row as UnfurlItem).id}
          getRowClassName={getRowClassName}
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={onRowSelectionModelChange}
          getRowSpacing={() => ({
            top: 1,
            bottom: 1,
          })}
          // Show all rows on single page with virtualization

          // Performance optimizations for large datasets

          // Remove footer completely

          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
          initialState={{
            sorting: {
              sortModel: [
                {
                  field: SORT_OPTIONS[DEFAULT_SORT_OPTION].field,
                  sort: SORT_OPTIONS[DEFAULT_SORT_OPTION].sort,
                },
              ],
            },
          }}
        />
      </div>
    );
  },
);
MediaDataGridInner.displayName = 'MediaDataGridComponent';

const MediaDataGridComponent = observer(MediaDataGridInner);

export const MediaDataGrid = MediaDataGridComponent;
